// How a title version reads on the scripts screens (mockups/script-review.html): the stage chip, the
// history lines, a render failure's reason, and what the plan says about the title. Shared by the
// scripts list, the review pane and the versions list.
import { Icon } from "@waypoint/surface";
import { t, type MessageKey } from "../../i18n/t.js";
import { StatusChip, type ChipTone } from "../../ui/form.js";
import type { Stage, TrailEntry } from "../../versions/model.js";
import { mmss, type PlanRead } from "./plan-model.js";

/** What the production plan says about one title: its name and runtimes, and what holds it. */
export interface TitleInfo {
  readonly packId: string;
  readonly titleId: string;
  readonly title: string;
  readonly tier: string | undefined;
  /** The script's planned runtime, in seconds; 0 where the plan has none. */
  readonly planned: number;
  /** The measured runtime, once a version has rendered. */
  readonly measured: number | undefined;
  /** The open contradictions linked to it (the plan summary's `blocked`; ADR-031). */
  readonly held: readonly string[];
}

/** The plan's facts for `titleId` (in `packId` where known), or the bare id where it has none. */
export function titleInfoOf(
  plan: PlanRead | undefined,
  titleId: string,
  packId?: string,
): TitleInfo {
  const here = (c: { titleId: string; packId: string }) =>
    c.titleId === titleId && (packId === undefined || c.packId === packId);
  const entry = plan?.catalogue.find(here);
  const summary = plan?.summary.segments.find(here);
  return {
    packId: entry?.packId ?? summary?.packId ?? "",
    titleId,
    title: entry?.title ?? titleId,
    tier: entry?.tier,
    planned: entry?.plannedRuntimeSeconds ?? 0,
    measured: entry?.runtimeSeconds,
    held: summary?.blocked ?? [],
  };
}

const STAGE_TONE: Readonly<Record<Stage | "none", ChipTone>> = {
  none: "quiet",
  draft: "plain",
  "sent-back": "stop",
  waiting: "open",
  held: "stop",
  rendering: "done",
  ready: "open",
  failed: "stop",
  live: "done",
  retired: "quiet",
  cancelled: "quiet",
};

/** The mockup's state chip. A ready or live title names its measured runtime where the plan has it. */
export function StageChip({
  stage,
  seconds,
  drafter = false,
}: {
  stage: Stage | "none";
  seconds?: number;
  /** The viewer drafted it: a script in review waits for someone else. */
  drafter?: boolean;
}) {
  const words =
    stage === "waiting" && drafter
      ? t("stage.waitingOther")
      : stage === "ready" && seconds !== undefined
        ? t("stage.readyTime", { time: mmss(seconds) })
        : stage === "live" && seconds !== undefined
          ? t("stage.liveTime", { time: mmss(seconds) })
          : t(`stage.${stage}`);
  return (
    <StatusChip tone={STAGE_TONE[stage]}>
      {stage === "held" || stage === "failed" ? <Icon name="alert" size={12} /> : null}
      {stage === "live" ? <Icon name="check" size={12} /> : null}
      {words}
    </StatusChip>
  );
}

/** The segment id badge the mockup draws beside a title (.seg). */
export function Seg({ id }: { id: string }) {
  return (
    <span className="rounded-4 border border-wp-line-strong bg-wp-bg/58 px-1.5 py-px font-mono text-11 font-semibold text-wp-text-2">
      {id}
    </span>
  );
}

/**
 * Who did it: "you" for the viewer, "a colleague" for anyone else. Waypoint stores no names (D-62),
 * so the id the API recorded is only ever compared, never shown.
 */
export function whoOf(by: string, userId: string): string {
  return by === userId ? t("review.you") : t("review.colleague");
}

/** One history line in words. */
export function trailWords(entry: TrailEntry, userId: string): string {
  const who = whoOf(entry.by, userId);
  switch (entry.kind) {
    case "sentBack":
      return t("review.trail.sentBack", { who, note: entry.note ?? "" });
    case "retired":
      return t("review.trail.retired");
    default:
      return t(`review.trail.${entry.kind}`, { who });
  }
}

type FailureKey = Exclude<Extract<MessageKey, `renderFailure.${string}`>, "renderFailure.other">;

const FAILURE_KEYS: ReadonlySet<string> = new Set<FailureKey>([
  "renderFailure.provider-failed",
  "renderFailure.provider-timeout",
  "renderFailure.still-missing",
  "renderFailure.invalid-script",
  "renderFailure.cancelled",
  "renderFailure.quota",
  "renderFailure.invalid-request",
  "renderFailure.still-mismatch",
  "renderFailure.submit-failed",
  "renderFailure.poll-budget",
  "renderFailure.hash-mismatch",
  "renderFailure.asset-unavailable",
  "renderFailure.invalid-result",
  "renderFailure.copy-failed",
  "renderFailure.version-not-approved",
]);

const isFailureKey = (key: string): key is FailureKey => FAILURE_KEYS.has(key);

/** A render failure's typed reason (render/job.ts RENDER_FAILURE_REASONS) in plain words. */
export function failureWords(reason: string): string {
  const key = `renderFailure.${reason}`;
  return isFailureKey(key) ? t(key) : t("renderFailure.other", { code: reason });
}
